<script setup lang="ts">
import type { StickerFill, StickerTilt } from './SfgSticker.vue'

/**
 * SfgStickerPill
 *
 * Wide-rectangle sticker with a fully-rounded pill profile. Same
 * die-cut white edge + chunky offset shadow as the other sticker
 * primitives, but the inner fill and border radius are pill-shaped
 * so two-word labels like CHALLENGE WHEEL fit on one line without
 * bursting out of a circular badge.
 *
 * Default: chalk fill, ink border, ink text.
 */

const props = withDefaults(defineProps<{
  fill?: StickerFill
  label: string
  scale?: number | string
  tilt?: StickerTilt
}>(), {
  fill: 'chalk',
  scale: undefined,
  tilt: 2,
})

const fillVar = computed(() => `var(--color-brand-${props.fill})`)
const textColor = computed(() => (props.fill === 'ink' ? 'var(--color-brand-yellow)' : 'var(--color-brand-ink)'))
const transform = computed(() => {
  const rotate = `rotate(var(--sticker-rotate-${props.tilt}))`
  if (props.scale === undefined) {
    return rotate
  }
  return `${rotate} scale(${props.scale})`
})
</script>

<template>
  <div
    class="inline-block rounded-full bg-white"
    :style="{
      padding: 'var(--sticker-edge)',
      boxShadow: 'var(--shadow-sticker)',
      transform,
    }"
  >
    <div
      class="flex items-center justify-center rounded-full px-6 py-2.5"
      :style="{
        background: fillVar,
        color: textColor,
        border: '2px solid var(--color-brand-ink)',
      }"
    >
      <span class="font-display uppercase text-[13px] tracking-widest leading-none whitespace-nowrap">
        {{ label }}
      </span>
    </div>
  </div>
</template>
